/**
 * Print-Only Advisory Team Guide for PDF Generation
 * Renders comprehensive guidance on professional advisors for business owners
 *
 * URL: /print/advisory-guide
 */

import React, { useEffect } from 'react';
import '../../print.css';

const PrintAdvisoryGuide = () => {
  useEffect(() => {
    // Mark as ready for PDF generation
    document.body.setAttribute('data-pdf-ready', 'true');
    document.title = 'Advisory Team Guide';
  }, []);

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Comprehensive advisor information for common advisors
  const commonAdvisors = [
    {
      title: 'Business Attorney',
      description: 'A business attorney provides legal guidance on corporate structure, contracts, compliance, and risk mitigation.',
      essentialExperience: [
        'Experience with business transactions, M&A deals, and exit planning',
        'Knowledge of corporate law, securities regulations, and business entity structures',
        'Track record of representing business owners through exits or sales',
        'Understanding of employment law, intellectual property, and contract negotiation'
      ],
      knowledgeAreas: [
        'Entity formation and corporate governance',
        'Contract drafting and negotiation',
        'Merger and acquisition transactions',
        'Intellectual property protection',
        'Employment and labor law compliance',
        'Commercial real estate transactions',
        'Risk management and liability protection'
      ],
      questionsToAsk: [
        'How many business exits or sales have you guided clients through?',
        'What is your experience with businesses in my industry?',
        'Do you have M&A transaction experience, or will you refer that work?',
        'Can you provide references from business owner clients?',
        'What is your fee structure - hourly, flat fee, or retainer?',
        'Who will actually handle my work - you or an associate?',
        'How quickly do you typically respond to urgent matters?',
        'What proactive legal strategies do you recommend for business owners planning an exit?'
      ],
      redFlags: [
        'Primarily handles personal injury or criminal law with limited business experience',
        'Cannot provide references from business owner clients',
        'Unclear or evasive about M&A experience',
        'Reluctant to discuss fee structures upfront',
        'Never asks about your business goals or exit timeline'
      ]
    },
    {
      title: 'CPA / Accountant',
      description: 'A qualified CPA manages your financial records, tax strategy, and provides critical financial insights for exit planning.',
      essentialExperience: [
        'CPA certification with active license in good standing',
        'Experience working with businesses in your size range and industry',
        'Track record of tax planning strategies that optimize business value',
        'Knowledge of financial statement preparation for due diligence',
        'Experience with quality of earnings reports and transaction support'
      ],
      knowledgeAreas: [
        'Tax planning and compliance (federal, state, and local)',
        'Financial statement preparation and analysis',
        'Entity structure optimization for tax efficiency',
        'Quality of earnings and EBITDA adjustments',
        'Transaction advisory and due diligence support',
        'Cash flow management and forecasting',
        'Internal controls and accounting systems'
      ],
      questionsToAsk: [
        'How many clients have you supported through a business sale or exit?',
        'What is your approach to proactive tax planning vs. compliance-only work?',
        'Can you help prepare our financials for buyer due diligence?',
        'What strategies do you recommend to maximize after-tax proceeds from a sale?',
        'How do you charge - monthly retainer, hourly, or project-based?',
        'What accounting software do you recommend and support?',
        'How often will we meet to review financials and discuss strategy?',
        'Do you work with a transaction advisory team, or can you refer specialists?'
      ],
      redFlags: [
        'Only provides annual tax return preparation without ongoing planning',
        'Cannot articulate strategies for maximizing business value',
        'Unfamiliar with quality of earnings or EBITDA adjustments',
        'Does not ask about your business goals or exit plans',
        'Slow to respond or return calls during critical periods'
      ]
    },
    {
      title: 'Financial Advisor / Wealth Manager',
      description: 'A financial advisor helps you plan for personal financial goals, investment strategy, and wealth preservation before and after an exit.',
      essentialExperience: [
        'CFP (Certified Financial Planner) or similar credential',
        'Experience working with business owners who have sold companies',
        'Knowledge of concentrated wealth strategies and exit planning',
        'Understanding of investment management and risk mitigation',
        'Ability to coordinate with tax and legal advisors'
      ],
      knowledgeAreas: [
        'Comprehensive financial planning and goal setting',
        'Investment portfolio management and asset allocation',
        'Retirement income planning and cash flow modeling',
        'Estate planning integration and wealth transfer strategies',
        'Risk management and insurance planning',
        'Tax-efficient investment strategies',
        'Liquidity event planning and windfall management'
      ],
      questionsToAsk: [
        'How many business owners have you worked with through a sale or exit?',
        'What is your approach to managing concentrated wealth from a business sale?',
        'Are you a fiduciary, and how are you compensated?',
        'How do you coordinate with my CPA and attorney?',
        'What is your investment philosophy and approach to risk management?',
        'Can you help model different exit scenarios and their financial impact?',
        'Do you provide comprehensive financial planning beyond investment management?',
        'What happens to my wealth if something happens to you or your firm?'
      ],
      redFlags: [
        'Primarily focused on selling investment products rather than planning',
        'Cannot articulate experience with liquidity events or business owners',
        'Unwilling to work collaboratively with other advisors',
        'Not registered as a fiduciary or unclear about compensation',
        'Promises unrealistic investment returns or "guaranteed" strategies'
      ]
    },
    {
      title: 'Exit / M&A Advisor',
      description: 'An exit or M&A advisor specializes in preparing businesses for sale and guiding owners through the transaction process.',
      essentialExperience: [
        'Track record of completed transactions in your industry and business size',
        'Knowledge of valuation methodologies and market conditions',
        'Experience managing the entire transaction process from preparation to closing',
        'Network of qualified buyers and deal professionals',
        'Understanding of deal structure, negotiation, and due diligence'
      ],
      knowledgeAreas: [
        'Business valuation and market positioning',
        'Buyer identification and qualification',
        'Deal structure and negotiation strategies',
        'Due diligence process management',
        'Transaction documentation and closing procedures',
        'Marketing and positioning businesses for sale',
        'Post-closing transition planning'
      ],
      questionsToAsk: [
        'How many businesses have you sold in my industry and size range?',
        'What is your average time to close and success rate?',
        'How do you value businesses, and what valuation do you expect for mine?',
        'What is your fee structure - success fee percentage, retainer, or both?',
        'Can you provide references from recent clients?',
        'How do you identify and qualify potential buyers?',
        'What preparation work do you recommend before going to market?',
        'How involved will you be in negotiations and due diligence?'
      ],
      redFlags: [
        'Cannot provide verifiable references from recent transactions',
        'Overpromises on valuation without thorough analysis',
        'Charges only hourly or large upfront fees with no success-based component',
        'Limited experience in your industry or business size',
        'Poor communication or unavailability during critical process stages'
      ]
    },
    {
      title: 'Insurance Agent / Risk Advisor',
      description: 'A risk advisor evaluates business and personal insurance needs to protect value and ensure adequate coverage for potential liabilities.',
      essentialExperience: [
        'Experience with commercial insurance and business risk assessment',
        'Knowledge of liability coverage, key person insurance, and business continuity',
        'Understanding of personal insurance needs for high-net-worth individuals',
        'Ability to review and compare policies from multiple carriers',
        'Track record of handling claims and advocating for clients'
      ],
      knowledgeAreas: [
        'Commercial general liability insurance',
        'Professional liability and errors & omissions coverage',
        'Directors and officers (D&O) insurance',
        'Key person and buy-sell agreement insurance',
        'Property and casualty coverage',
        'Cyber liability and data breach insurance',
        'Umbrella and excess liability policies',
        'Personal insurance for business owners (life, disability, long-term care)'
      ],
      questionsToAsk: [
        'What types of businesses do you primarily work with?',
        'How do you assess risk and determine appropriate coverage levels?',
        'Do you work with multiple carriers, or are you captive to one company?',
        'What is your process for handling claims?',
        'How often do you review and update coverage?',
        'What insurance should I have in place before selling my business?',
        'Do you coordinate with my other advisors on insurance strategy?',
        'What are the most common coverage gaps you see in businesses like mine?'
      ],
      redFlags: [
        'Only sells products from one insurance company',
        'Pushes products without understanding your business and risks',
        'Cannot explain policy exclusions and limitations clearly',
        'Does not conduct regular coverage reviews',
        'Unresponsive when claims need to be filed'
      ]
    },
    {
      title: 'Banker / Lender',
      description: 'A banker provides financing solutions, manages banking relationships, and may offer treasury management and investment banking services.',
      essentialExperience: [
        'Experience working with businesses in your industry and size',
        'Knowledge of various lending products and financing structures',
        'Relationship management and proactive communication',
        'Understanding of working capital needs and business growth financing',
        'Access to specialized services like treasury management or investment banking'
      ],
      knowledgeAreas: [
        'Commercial lending and credit analysis',
        'Lines of credit and term loans',
        'Equipment financing and SBA loans',
        'Cash management and treasury services',
        'Merchant services and payment processing',
        'Foreign exchange and international banking',
        'Investment banking and capital raising (if applicable)'
      ],
      questionsToAsk: [
        'What types of businesses do you typically work with?',
        'What lending products and services does your bank offer?',
        'How do you approach covenant structures and financial requirements?',
        'What is your credit approval process and turnaround time?',
        'Do you offer treasury management and cash flow optimization services?',
        'How can you support our business as we prepare for an exit?',
        'What are your typical loan terms, rates, and fees?',
        'Who will be our primary contact, and how accessible will you be?'
      ],
      redFlags: [
        'Only interested in transactional relationships, not advisory partnerships',
        'Inflexible on lending terms without understanding business context',
        'Poor communication or slow response to inquiries',
        'Cannot explain credit terms or covenants clearly',
        'Frequent turnover in relationship managers'
      ]
    },
    {
      title: 'Estate Planner / Tax Attorney',
      description: 'An estate planner or tax attorney helps structure wealth transfer, minimize estate taxes, and ensure assets are distributed according to your wishes.',
      essentialExperience: [
        'Specialization in estate planning and/or tax law',
        'Experience with business succession and ownership transfers',
        'Knowledge of trusts, gifting strategies, and estate tax mitigation',
        'Understanding of charitable planning and philanthropic structures',
        'Ability to coordinate with financial advisors and CPAs'
      ],
      knowledgeAreas: [
        'Wills, trusts, and powers of attorney',
        'Estate and gift tax planning',
        'Business succession planning',
        'Charitable planning and donor-advised funds',
        'Asset protection strategies',
        'Generation-skipping transfer strategies',
        'Trust administration and probate'
      ],
      questionsToAsk: [
        'What percentage of your practice is dedicated to estate planning?',
        'How many business owners have you helped with succession planning?',
        'What strategies do you recommend for minimizing estate taxes?',
        'How do you integrate business value into an overall estate plan?',
        'What is your approach to protecting assets from creditors and lawsuits?',
        'How do you coordinate with my CPA and financial advisor?',
        'What are your fees for estate planning services?',
        'How often should we review and update our estate plan?'
      ],
      redFlags: [
        'Estate planning is not their primary practice area',
        'Uses outdated or overly complex strategies without clear explanation',
        'Unwilling to coordinate with other advisors',
        'Pushes insurance products as primary estate planning solution',
        'Cannot explain recommendations in plain language'
      ]
    },
    {
      title: 'Business Coach / Consultant',
      description: 'A business coach or consultant provides strategic guidance, accountability, and expertise to help you improve operations and prepare for an exit.',
      essentialExperience: [
        'Track record of working with businesses in your industry or stage',
        'Demonstrated expertise in areas critical to your business (operations, sales, marketing, etc.)',
        'Experience preparing businesses for sale or transition',
        'Strong communication and coaching skills',
        'Results-oriented with measurable outcomes'
      ],
      knowledgeAreas: [
        'Strategic planning and business model development',
        'Operational efficiency and process improvement',
        'Sales and marketing strategy',
        'Leadership development and team building',
        'Financial management and profitability improvement',
        'Exit readiness and value acceleration',
        'Change management and organizational development'
      ],
      questionsToAsk: [
        'What is your background and expertise in my industry?',
        'Can you provide case studies or references from similar businesses?',
        'What is your coaching methodology and approach?',
        'How do you measure success and track progress?',
        'What is your fee structure and engagement length?',
        'How often will we meet, and what is your availability between sessions?',
        'Have you helped businesses prepare for and complete exits?',
        'What specific areas do you believe we should focus on first?'
      ],
      redFlags: [
        'Promises quick fixes or unrealistic results',
        'Vague or unverifiable track record',
        'No clear methodology or framework',
        'Poor listening skills or prescriptive approach without understanding your business',
        'Cannot articulate measurable outcomes or success metrics'
      ]
    }
  ];

  // Less common but potentially valuable advisors
  const lessCommonAdvisors = [
    {
      title: 'Fractional CFO',
      description: 'Part-time financial executive who provides high-level financial strategy, reporting, and leadership without the cost of a full-time CFO.',
      whenNeeded: 'When you need strategic financial leadership but cannot justify a full-time CFO. Essential for businesses preparing for sale to ensure financials are investor-ready.'
    },
    {
      title: 'Investment Banker',
      description: 'Specialized advisor for larger transactions (typically $10M+) who manages the sale process, identifies strategic buyers, and maximizes valuation.',
      whenNeeded: 'For businesses with enterprise value above $10-20M or complex transactions requiring institutional buyer networks and sophisticated deal structures.'
    },
    {
      title: 'Valuation Expert',
      description: 'Professional who provides formal business valuations for tax, transaction, or strategic planning purposes.',
      whenNeeded: 'When you need a defensible valuation for estate planning, gift tax purposes, buy-sell agreements, or initial exit planning discussions.'
    },
    {
      title: 'HR Consultant / Employment Attorney',
      description: 'Specialist in employment law, HR compliance, and workforce management.',
      whenNeeded: 'When facing employee relations issues, preparing employee handbooks, or ensuring compliance with employment laws before a transaction.'
    },
    {
      title: 'Cybersecurity Consultant',
      description: 'Expert in protecting digital assets, data security, and compliance with privacy regulations.',
      whenNeeded: 'For businesses handling sensitive customer data or when buyers require evidence of robust cybersecurity practices during due diligence.'
    },
    {
      title: 'Intellectual Property Attorney',
      description: 'Specialist in patents, trademarks, copyrights, and trade secret protection.',
      whenNeeded: 'When IP is a significant component of business value, or when protecting proprietary technology, brands, or creative works.'
    },
    {
      title: 'Supply Chain Consultant',
      description: 'Expert in optimizing procurement, logistics, inventory management, and vendor relationships.',
      whenNeeded: 'For manufacturing, distribution, or retail businesses where supply chain efficiency significantly impacts profitability and valuation.'
    },
    {
      title: 'Marketing Strategist / Agency',
      description: 'Professional who develops and executes marketing strategies to drive growth and build brand value.',
      whenNeeded: 'When revenue growth has stalled, digital presence is weak, or marketing lacks strategic direction. Critical for businesses targeting revenue growth before exit.'
    },
    {
      title: 'Operations Consultant',
      description: 'Specialist in process improvement, efficiency optimization, and operational excellence.',
      whenNeeded: 'When operations are chaotic, margins are compressed, or buyers are likely to scrutinize operational efficiency during due diligence.'
    },
    {
      title: 'Quality of Earnings (QofE) Provider',
      description: 'Accounting firm that provides independent analysis of earnings quality and financial adjustments for transactions.',
      whenNeeded: 'Typically engaged by buyers during due diligence, but sellers can benefit from a pre-transaction QofE to identify and address issues early.'
    },
    {
      title: 'Environmental Consultant',
      description: 'Expert in environmental compliance, site assessments, and regulatory requirements.',
      whenNeeded: 'For businesses in manufacturing, real estate, or industries with environmental risk. Required for Phase I and Phase II environmental assessments in most transactions.'
    },
    {
      title: 'International Tax Specialist',
      description: 'CPA or attorney with expertise in cross-border tax issues and international business structures.',
      whenNeeded: 'For businesses with international operations, foreign ownership, or buyers/sellers outside the United States.'
    }
  ];

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Advisory Team Guide</h1>
          <div className="report-type">Essential Guidance for Selecting Professional Advisors</div>
        </div>

        <div className="cover-business-info">
          <h2>Building Your Exit Planning Team</h2>
          <p className="industry">Comprehensive criteria and questions to identify qualified advisors</p>
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Professional Advisory Resource Guide</p>
        </div>
      </div>

      {/* Introduction Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Why Your Advisory Team Matters</h1>

        <div style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.15in' }}>
          <p style={{ marginBottom: '0.1in' }}>
            A strong advisory team is one of the most valuable assets you can build as a business owner planning an exit.
            The right advisors will help you maximize business value, minimize taxes, navigate complex transactions, and achieve
            your personal and financial goals.
          </p>
          <p style={{ marginBottom: '0.15in' }}>
            This guide provides comprehensive criteria for selecting qualified advisors who can support your exit journey.
            For each advisor type, you'll find essential experience requirements, key knowledge areas, evaluation questions,
            and warning signs to watch for.
          </p>
        </div>

        <div className="disclosure-box">
          <h3>Best Practices for Building Your Advisory Team</h3>
          <ol style={{ fontSize: '8.5pt', lineHeight: '1.5', marginLeft: '0.15in', marginTop: '0.05in' }}>
            <li style={{ marginBottom: '0.05in' }}>
              <strong>Start early:</strong> Build relationships with advisors 3-5 years before your planned exit, not 3-5 months.
            </li>
            <li style={{ marginBottom: '0.05in' }}>
              <strong>Check references:</strong> Always speak with at least 2-3 clients who have completed exits similar to yours.
            </li>
            <li style={{ marginBottom: '0.05in' }}>
              <strong>Ensure collaboration:</strong> Your advisors should be willing and able to work together as a coordinated team.
            </li>
            <li style={{ marginBottom: '0.05in' }}>
              <strong>Prioritize experience:</strong> Exit planning is not the time for advisors to learn on your dime. Seek proven expertise.
            </li>
            <li style={{ marginBottom: '0.05in' }}>
              <strong>Understand fees:</strong> Be clear on how each advisor is compensated and ensure fee structures align with your interests.
            </li>
            <li style={{ marginBottom: '0' }}>
              <strong>Trust your instincts:</strong> You should feel confident in your advisors' abilities and comfortable with their communication style.
            </li>
          </ol>
        </div>

        <h2 className="section-title" style={{ marginTop: '0.2in', fontSize: '11pt' }}>How to Use This Guide</h2>
        <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.1in' }}>
          Each advisor profile in this guide includes four key sections:
        </p>
        <ul style={{ fontSize: '8.5pt', lineHeight: '1.5', marginLeft: '0.15in', listStyle: 'disc' }}>
          <li style={{ marginBottom: '0.05in' }}>
            <strong>Essential Experience & Qualifications:</strong> The minimum credentials and background you should look for
          </li>
          <li style={{ marginBottom: '0.05in' }}>
            <strong>Key Knowledge Areas:</strong> Critical areas of expertise the advisor should possess
          </li>
          <li style={{ marginBottom: '0.05in' }}>
            <strong>Questions to Ask:</strong> Specific questions to help you evaluate potential advisors
          </li>
          <li>
            <strong>Red Flags:</strong> Warning signs that an advisor may not be the right fit
          </li>
        </ul>
      </div>

      {/* Each Common Advisor on Its Own Page */}
      {commonAdvisors.map((advisor, index) => (
        <div key={index} className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <h1 className="section-title" style={{ color: '#0d7377', marginBottom: '0.08in' }}>{advisor.title}</h1>

          <p style={{ fontSize: '9pt', lineHeight: '1.5', marginBottom: '0.12in', fontStyle: 'italic' }}>
            {advisor.description}
          </p>

          {/* Essential Experience */}
          <div style={{ marginBottom: '0.12in' }}>
            <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.06in', borderBottom: '2px solid #3b82f6', paddingBottom: '0.02in' }}>
              Essential Experience & Qualifications
            </h3>
            <ul style={{ fontSize: '8.5pt', lineHeight: '1.4', marginLeft: '0.15in', marginTop: '0.04in', listStyle: 'disc' }}>
              {advisor.essentialExperience.map((item, idx) => (
                <li key={idx} style={{ marginBottom: '0.04in' }}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Knowledge Areas */}
          <div style={{ marginBottom: '0.12in' }}>
            <h3 style={{ fontSize: '10pt', color: '#059669', marginBottom: '0.06in', borderBottom: '2px solid #10b981', paddingBottom: '0.02in' }}>
              Key Knowledge Areas
            </h3>
            <ul style={{ fontSize: '8.5pt', lineHeight: '1.4', marginLeft: '0.15in', marginTop: '0.04in', listStyle: 'disc' }}>
              {advisor.knowledgeAreas.map((item, idx) => (
                <li key={idx} style={{ marginBottom: '0.04in' }}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Questions to Ask */}
          <div style={{ marginBottom: '0.12in' }}>
            <h3 style={{ fontSize: '10pt', color: '#7c3aed', marginBottom: '0.06in', borderBottom: '2px solid #a78bfa', paddingBottom: '0.02in' }}>
              Questions to Ask When Evaluating
            </h3>
            <ul style={{ fontSize: '8.5pt', lineHeight: '1.4', marginLeft: '0.15in', marginTop: '0.04in', listStyle: 'disc' }}>
              {advisor.questionsToAsk.map((item, idx) => (
                <li key={idx} style={{ marginBottom: '0.04in', fontStyle: 'italic' }}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Red Flags */}
          <div className="disclosure-box" style={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444' }}>
            <h3 style={{ color: '#dc2626', marginBottom: '0.05in', fontSize: '10pt' }}> Red Flags to Watch For</h3>
            <ul style={{ fontSize: '8.5pt', lineHeight: '1.4', marginLeft: '0.15in', marginTop: '0.04in', listStyle: 'disc' }}>
              {advisor.redFlags.map((item, idx) => (
                <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      ))}

      {/* Less Common Advisors - Two Per Page */}
      {lessCommonAdvisors.reduce((pages, advisor, index) => {
        const pageIndex = Math.floor(index / 6);
        if (!pages[pageIndex]) {
          pages[pageIndex] = [];
        }
        pages[pageIndex].push(advisor);
        return pages;
      }, []).map((pageAdvisors, pageIndex) => (
        <div key={`less-common-${pageIndex}`} className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          {pageIndex === 0 && (
            <>
              <h1 className="section-title" style={{ marginBottom: '0.05in' }}>Less Common (But Often Valuable) Advisors</h1>
              <p style={{ fontSize: '8.5pt', marginBottom: '0.12in', fontStyle: 'italic' }}>
                Specialists you may need depending on your business, industry, or transaction complexity.
              </p>
            </>
          )}

          {pageAdvisors.map((advisor, idx) => (
            <div key={idx} style={{
              marginBottom: idx === 0 ? '0.15in' : '0',
              paddingBottom: '0.12in',
              borderBottom: idx === 0 ? '1px solid #e5e7eb' : 'none'
            }}>
              <h3 style={{ fontSize: '10pt', color: '#0d7377', marginBottom: '0.04in' }}>{advisor.title}</h3>
              <p style={{ fontSize: '8.5pt', lineHeight: '1.4', marginBottom: '0.06in' }}>
                {advisor.description}
              </p>
              <div style={{
                backgroundColor: '#fef3c7',
                borderLeft: '3px solid #f59e0b',
                padding: '0.06in',
                fontSize: '8pt',
                lineHeight: '1.4'
              }}>
                <strong>When You Need Them:</strong> {advisor.whenNeeded}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default PrintAdvisoryGuide;
